import { Controller } from '@hotwired/stimulus'
import zoid from 'zoid9031'
import * as analytics from '../utils/analytics'

export default class extends Controller {
  static get targets () {
    return ['link']
  }

  initialize () {
    // Don't run if we're not in an iframe
    if (window.self === window.top) return

    // If a version is present, legacy support isn't needed
    if (window.xprops && window.xprops.version) return
    console.debug('[bundle_embed_controller] initialized')

    window.ProvenanceEmbedComponent = zoid.create({
      tag: this.element.dataset.type,
      url: window.location.href, // window.location.href is the Bundle frame, not to be confused with the modal frame (link.dataset.href)
      dimensions: {
        width: '100%',
        height: '90px'
      },
      autoResize: {
        height: true,
        width: false
      }
    })
  }

  connect () {
    // Don't run if we're not in an iframe
    if (window.self === window.top) return

    // If a version is present, legacy support isn't needed
    if (window.xprops && window.xprops.version) {
      this.setupImpressionTracking()
      return
    }
    console.debug('[bundle_embed_controller] connected')

    const proofPoints = []
    this.proofPointNames = []

    this.linkTargets.forEach(link => {
      const { href, name, proofPointType, slug } = link.dataset
      proofPoints.push({
        url: href,
        proofPointName: name,
        proofPointType,
        tag: this.getSlug(slug)
      })

      this.proofPointNames.push(name)
    })

    window.xprops.initModals(proofPoints, this.element.dataset.subjectName, this.element.dataset.brandName, this.element.dataset.channel)
  }

  getSlug (slug) {
    if (slug) {
      return slug.toLowerCase()
    } else {
      console.error('[bundle_embed_controller] no slug found in element\'s dataset.')
    }
  }

  // this could also be in analytics controller to further unify code
  setupImpressionTracking () {
    this.defineParentEventHandler()

    window.xprops.onChildEvent({
      name: 'setup_impression_tracking'
    })
  }

  // consider moving this to something like sdkEvents so we can have onParentEvent defined in a single place
  defineParentEventHandler () {
    window.xprops.export({
      onParentEvent: (event) => {
        switch (event.name) {
          case 'impression_detected':
            this.trackImpression()
            break
          default:
            console.warn('[Provenance] Unrecognised parent event:', event)
        }
      }
    })
  }

  getCombinedBundlePPsInView () {
    // get tab with aria selected
    const proofPointNames = []
    const tabName = this.element.querySelector("[aria-selected='true']").dataset.name
    const panel = (tabName === 'product-pp-tab')
      ? this.element.querySelector("[data-name='product-pp-panel']")
      : this.element.querySelector("[data-name='brand-pp-panel']")
    const proofPoints = panel.querySelectorAll('button')
    proofPoints.forEach(pp => {
      proofPointNames.push(pp.dataset.name)
    })
    return proofPointNames
  }

  // this could also be in analytics controller to further unify code
  trackImpression () {
    const { channel, brandName, subjectName, type } = this.element.dataset
    const eventLabel = `${channel} / ${brandName} / ${subjectName}`
    let prodOrBrand
    let dtc

    if (subjectName && brandName) {
      prodOrBrand = (subjectName === brandName) ? 'brand' : 'product'
    }
    if (channel && brandName) {
      dtc = channel === brandName
    }
    // if combined bundle pp impressions tracked here, it will be product
    const ppType = (type === 'combined_bundle') ? 'product' : prodOrBrand
    const bundleType = (type === 'combined_bundle') ? 'combined_bundle' : prodOrBrand

    let proofPointNames =
        this.linkTargets.map(link => {
          return link.dataset.name
        })

    analytics.track('impression', {
      eventCategory: 'impression_bundle',
      eventAction: proofPointNames.join('|'),
      eventLabel
    }, { adapter: 'gtm' })

    analytics.track('bundle_impression', {
      object: 'bundle',
      action: 'impression',
      channel,
      brand_name: brandName,
      subject_name: subjectName,
      subject_value: proofPointNames.join('|'),
      subject_type: bundleType,
      event_label: eventLabel,
      bundle_type: bundleType,
      dtc
    }, { adapter: 'segment' })

    // if the bundle is combined, only track proof points that are in view
    if (type === 'combined_bundle') {
      proofPointNames = this.getCombinedBundlePPsInView()
    }

    proofPointNames.forEach((name) => {
      analytics.track('impression', {
        eventCategory: 'impression_proofpoint_bundle',
        eventAction: name,
        eventLabel
      }, { adapter: 'gtm' })

      analytics.track('proofpoint_impression', {
        object: 'proofpoint',
        action: 'impression',
        channel,
        brand_name: brandName,
        subject_name: subjectName,
        subject_value: name,
        subject_type: ppType,
        bundle_type: bundleType,
        dtc
      }, { adapter: 'segment' })
    })
  }

  open (event) {
    if (window.xprops && window.xprops.version) return

    // FIXME: Calling openModal here should be unnecessary, since
    // it's now handled by modal_trigger_controller.js
    // needed for backward compatibility (SDK < v0.8)
    console.debug('[bundle_embed_controller] opening modal (legacy)', event.currentTarget.dataset.href)
    window.xprops.openModal(event.currentTarget.dataset.href)
  }
}
